/**
 * admin.js - Administration API service.
 *
 * Covers system health, business rules, currencies, user management,
 * backup & restore, active sessions, security notes, and sync history.
 *
 * All functions return Promises and are designed to be called from
 * the Admin page component.
 */

import axios from "axios";
import { API_BASE } from "../config.js";
import { getAccessToken } from "../auth/apiAuth.jsx";

const api = axios.create({
  baseURL: `${API_BASE}/api/admin`,
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const tk = getAccessToken && getAccessToken();
  if (tk) config.headers.Authorization = `Bearer ${tk}`;
  return config;
});

/* ------------------------------------------------------------------ */
/* Health                                                              */
/* ------------------------------------------------------------------ */

/**
 * Get system health overview.
 * @returns {Promise<Object>} { tiles, recordCounts, serviceInfo, ... }
 */
export async function getHealth() {
  const response = await api.get("/health");
  return response.data;
}

/* ------------------------------------------------------------------ */
/* Business rules                                                      */
/* ------------------------------------------------------------------ */

/**
 * Get current business rules / scoring parameters.
 * @returns {Promise<Object>} Business rules object
 */
export async function getBusinessRules() {
  const response = await api.get("/rules");
  return response.data;
}

/**
 * Save updated business rules.
 * @param {Object} rules - Updated rules object
 * @returns {Promise<Object>} Saved rules (server-validated)
 */
export async function saveBusinessRules(rules) {
  const response = await api.put("/rules", rules);
  return response.data;
}

/* ------------------------------------------------------------------ */
/* Currencies                                                          */
/* ------------------------------------------------------------------ */

/**
 * Get all configured currencies and their exchange rates.
 * @returns {Promise<Array>} List of { code, rate }
 */
export async function getCurrencies() {
  const response = await api.get("/currencies");
  return response.data || [];
}

/**
 * Create or update a currency exchange rate.
 * @param {Object} currency - { code: string, rate: number }
 * @returns {Promise<Object>} Saved currency record
 */
export async function saveCurrency(currency) {
  const response = await api.put(`/currencies/${currency.code}`, currency);
  return response.data;
}

/**
 * Delete a currency.
 * @param {string} code - Currency code to delete
 * @returns {Promise<void>}
 */
export async function deleteCurrency(code) {
  await api.delete(`/currencies/${code}`);
}

/* ------------------------------------------------------------------ */
/* Users                                                               */
/* ------------------------------------------------------------------ */

/**
 * Get all user accounts.
 * @returns {Promise<Array>} List of user records
 */
export async function getUsers() {
  const response = await api.get("/users");
  return response.data || [];
}

/**
 * Create a new user account.
 * @param {Object} userData - { username, fullName, email, role, password, active }
 * @returns {Promise<Object>} Created user record
 */
export async function createUser(userData) {
  const response = await api.post("/users", userData);
  return response.data;
}

/**
 * Update an existing user account.
 * @param {string} username - Username (primary key)
 * @param {Object} userData - Fields to update
 * @returns {Promise<Object>} Updated user record
 */
export async function updateUser(username, userData) {
  const response = await api.put(`/users/${username}`, userData);
  return response.data;
}

/* ------------------------------------------------------------------ */
/* Backup & restore                                                    */
/* ------------------------------------------------------------------ */

/**
 * Get list of available backups.
 * @returns {Promise<Array>} List of backup records { createdAt, fileName, size, contents }
 */
export async function getBackups() {
  const response = await api.get("/backups");
  return response.data || [];
}

/**
 * Create a new backup now.
 * @returns {Promise<Object>} Created backup record
 */
export async function createBackup() {
  const response = await api.post("/backups");
  return response.data;
}

/**
 * Export all system data as a downloadable blob.
 * @returns {Promise<Blob>} JSON blob of all data
 */
export async function exportAll() {
  const response = await api.get("/export", {
    responseType: "blob",
  });
  return response.data;
}

/**
 * Restore from a specific backup.
 * @param {string} backupId - Backup file name or ID
 * @returns {Promise<Object>} Restore result
 */
export async function restoreBackup(backupId) {
  const response = await api.post(`/backups/${backupId}/restore`);
  return response.data;
}

/* ------------------------------------------------------------------ */
/* Sessions                                                            */
/* ------------------------------------------------------------------ */

/**
 * Get all active sessions.
 * @returns {Promise<Array>} List of session records
 */
export async function getSessions() {
  const response = await api.get("/sessions");
  return response.data || [];
}

/**
 * Revoke (force sign-out) a specific session.
 * @param {string} sessionId - Session identifier
 * @returns {Promise<void>}
 */
export async function revokeSession(sessionId) {
  await api.delete(`/sessions/${sessionId}`);
}

/* ------------------------------------------------------------------ */
/* Security notes                                                      */
/* ------------------------------------------------------------------ */

/**
 * Get security features and recommendations (read-only).
 * @returns {Promise<Object>} { features: Array, recommendations: Array }
 */
export async function getSecurityNotes() {
  const response = await api.get("/security");
  return response.data;
}

/* ------------------------------------------------------------------ */
/* Sync History                                                        */
/* ------------------------------------------------------------------ */

/**
 * Get sync status and history.
 * @returns {Promise<Object>} { status, counts, branches, vendorsByCompany }
 */
export async function getSyncHistory() {
  const response = await api.get("/sync-history");
  return response.data;
}

/**
 * Get per-branch sync breakdown.
 * @returns {Promise<Array>} Array of branch sync objects
 */
export async function getSyncBranches() {
  const response = await api.get("/sync/branches");
  return response.data;
}

/**
 * Get product sync history with pagination.
 * @param {Object} params - { page, perPage, company, search }
 * @returns {Promise<Object>} { summary, pagination, products }
 */
export async function getProductSyncHistory(params = {}) {
  const response = await api.get("/sync-history/products", { params });
  return response.data;
}
